<template>
  <div class="asignar-boleto-wrapper">
    <div class="asignar-boleto-card">
      <!-- Cargando -->
      <div v-if="cargando" class="estado-center">
        <div class="spinner-border text-primary mb-3" role="status"></div>
        <p class="text-muted">Cargando peleadores confirmados...</p>
      </div>

      <!-- Sin peleadores disponibles -->
      <div v-else-if="sinPeleadores" class="estado-center">
        <div class="estado-icon">😕</div>
        <h4>Sin peleadores disponibles</h4>
        <p class="text-muted">
          Por el momento no hay peleadores confirmados para registrar esquinas.
        </p>
      </div>

      <!-- Registro exitoso -->
      <div v-else-if="registrado" class="estado-center">
        <div class="estado-icon">🥋</div>
        <h4>¡Registro exitoso!</h4>
        <p>
          Quedaste registrado como esquina de
          <strong>{{ peleadoresSeleccionadosTexto }}</strong
          >.
        </p>
        <p class="text-muted" style="font-size: 0.85rem">
          Preséntate el día del evento con una identificación.
        </p>
      </div>

      <!-- Formulario -->
      <form v-else @submit.prevent="enviar">
        <h3 class="form-titulo">Registro de asistente / esquina</h3>
        <p class="form-subtitulo">
          {{ nombreEvento }} · Acompaña a tu peleador el día del evento
        </p>

        <!-- Nombre -->
        <div class="mb-3">
          <label class="form-label">Nombre *</label>
          <input
            v-model="form.nombre"
            type="text"
            class="form-control"
            placeholder="Tu nombre"
            required
          />
        </div>

        <!-- Apellido -->
        <div class="mb-3">
          <label class="form-label">Apellido *</label>
          <input
            v-model="form.apellido"
            type="text"
            class="form-control"
            placeholder="Tu apellido"
            required
          />
        </div>

        <!-- Correo -->
        <div class="mb-3">
          <label class="form-label">Correo electrónico *</label>
          <input
            v-model="form.correo"
            type="email"
            class="form-control"
            placeholder="tu@correo.com"
            required
          />
        </div>

        <!-- Selección de peleadores -->
        <div class="mb-3">
          <label class="form-label">
            ¿A quién vas a asistir? *
            <span class="form-hint">(puedes elegir varios)</span>
          </label>
          <div class="peleadores-grid">
            <label
              v-for="p in peleadores"
              :key="p.id"
              class="peleador-opcion"
              :class="{ 'is-selected': form.peleadores.includes(p.id) }"
            >
              <input
                type="checkbox"
                :value="p.id"
                v-model="form.peleadores"
                class="d-none"
              />
              <img :src="p.foto || avatarDefault" :alt="p.nombre" class="peleador-foto" />
              <span class="peleador-nombre">{{ p.nombre }} {{ p.apellido }}</span>
              <i
                v-if="form.peleadores.includes(p.id)"
                class="bi bi-check-circle-fill check-icon"
              ></i>
            </label>
          </div>
        </div>

        <!-- Error -->
        <div
          v-if="errorMsg"
          class="alert alert-danger py-2 mb-3"
          style="font-size: 0.9rem"
        >
          {{ errorMsg }}
        </div>

        <div class="d-grid">
          <button class="btn btn-primary btn-lg" type="submit" :disabled="enviando">
            <span v-if="enviando" class="spinner-border spinner-border-sm me-2"></span>
            {{ enviando ? "Registrando..." : "Confirmar registro" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import avatarDefault from "@/assets/img/Evolve/usuario.png";
import {
  getPeleadoresConfirmados,
  getEventoActivo,
  registrarAsistentePeleador,
} from "@/utils/api_evento";

// ── Estado de pantalla ────────────────────────────────────────────────────────
const cargando = ref(true);
const sinPeleadores = ref(false);
const registrado = ref(false);
const enviando = ref(false);
const errorMsg = ref("");

// ── Datos del evento ──────────────────────────────────────────────────────────
const nombreEvento = ref("");
const peleadores = ref([]);

// ── Formulario ────────────────────────────────────────────────────────────────
const form = ref({ nombre: "", apellido: "", correo: "", peleadores: [] });

const peleadoresSeleccionadosTexto = computed(() => {
  return peleadores.value
    .filter((p) => form.value.peleadores.includes(p.id))
    .map((p) => `${p.nombre} ${p.apellido}`)
    .join(", ");
});

// ── Al montar ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const [evento, listaPeleadores] = await Promise.all([
      getEventoActivo().catch(() => null),
      getPeleadoresConfirmados(),
    ]);
    nombreEvento.value = evento?.nombre || "";
    peleadores.value = listaPeleadores;
    if (!listaPeleadores.length) sinPeleadores.value = true;
  } catch (e) {
    sinPeleadores.value = true;
  } finally {
    cargando.value = false;
  }
});

// ── Enviar ────────────────────────────────────────────────────────────────────
async function enviar() {
  errorMsg.value = "";

  if (!form.value.peleadores.length) {
    errorMsg.value = "Selecciona al menos un peleador al que vayas a asistir.";
    return;
  }

  enviando.value = true;
  try {
    // Un registro por peleador seleccionado — el backend acepta un solo
    // peleador por llamada, sin restricción de correo repetido.
    await Promise.all(
      form.value.peleadores.map((peleadorId) =>
        registrarAsistentePeleador({
          nombre: form.value.nombre,
          apellido: form.value.apellido,
          correo: form.value.correo,
          peleador: peleadorId,
        })
      )
    );
    registrado.value = true;
  } catch (e) {
    const data = e.response?.data;
    if (data?.correo) errorMsg.value = data.correo[0];
    else if (data?.peleador) errorMsg.value = data.peleador[0];
    else if (data?.detail) errorMsg.value = data.detail;
    else errorMsg.value = "No fue posible completar el registro. Intenta de nuevo.";
  } finally {
    enviando.value = false;
  }
}
</script>

<style scoped>
.asignar-boleto-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url("@/assets/img/Evolve/img_7.jpg") center center / cover no-repeat;
  padding: 2rem;
}

.asignar-boleto-card {
  background: #fff;
  border-radius: 20px;
  padding: 2.5rem 2rem;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

/* Estados */
.estado-center {
  text-align: center;
  padding: 1rem 0;
}
.estado-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

/* Formulario */
.form-titulo {
  text-align: center;
  font-weight: 700;
  margin-bottom: 0.25rem;
}
.form-subtitulo {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.75rem;
}
.form-hint {
  font-weight: 400;
  color: #999;
  font-size: 0.78rem;
}

/* Selección de peleadores */
.peleadores-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px;
}
.peleador-opcion {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 6px;
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  text-align: center;
}
.peleador-opcion:hover {
  border-color: #aac4f5;
}
.peleador-opcion.is-selected {
  border-color: #007bff;
  background: #f0f6ff;
}
.peleador-foto {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #eee;
}
.peleador-nombre {
  font-size: 0.75rem;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}
.check-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  color: #007bff;
  font-size: 1rem;
  background: #fff;
  border-radius: 50%;
}
</style>
